'use client';

import { c as _c } from "react/compiler-runtime";
import type { MarketParams } from '@morpho-org/blue-sdk';
import type { InterfaceToken } from '@morpho-org/dapp-core';
import { MathLib } from '@morpho-org/blue-sdk';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useWeb3Context } from '@/contexts/Web3Context';
import { useStickyMaxInput } from '@/hooks/useStickyMaxInput';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import useAssetCanTransferOnchain from '../../useAssetCanTransferOnchain';
import { getLendWithdrawErrorMessage, shouldShowLendWithdrawLiquidityWarning } from './lendWithdrawError.helpers';
import { PREVIEW_DEBOUNCE_MS, shouldUseExactSharesForMaxLendWithdraw } from './marketAction.helpers';
import { ACCRUAL_BUFFER, computeMaxLendWithdrawAssets } from './marketMaxAmounts';
import { useMarketActionBuilder } from './useMarketActionBuilder';
import { useMarketBlue } from './useMarketBlue';
import { useMarketReallocations } from './useMarketReallocations';
export interface UseMarketLendWithdrawFormParams {
  marketParams: MarketParams | undefined;
  loanAsset: InterfaceToken | null | undefined;
}

/**
 * Form model behind the market-page lend-withdraw panel. The Position Manager dialog shares the
 * whole build/read layer (`useMarketActionBuilder`, `useMarketBlue`, `useMarketReallocations`,
 * `marketAction.helpers`, `marketMaxAmounts`, `lendWithdrawError.helpers`) but duplicates this
 * form-model orchestration, so the finalize gating is what can drift: the PM dialog does not
 * block on `positionError`, for one. Migrating it onto this hook is a follow-up.
 */
export function useMarketLendWithdrawForm(t0) {
  const $ = _c(74);
  const {
    marketParams,
    loanAsset
  } = t0;
  const {
    account,
    targetChain
  } = useWeb3Context();
  const {
    marketLendPosition
  } = useFeatureFlags();
  const [inputWithdrawAmount, setInputWithdrawAmount] = useState(ZERO);
  const isMarketLendEnabled = marketLendPosition && !!marketParams;
  const {
    positionData,
    error: positionError,
    isLoading: isPositionLoading,
    snapshotTimestamp
  } = useMarketBlue(marketParams);
  const currentSupply = positionData?.supplyAssets;
  let t1;
  if ($[0] !== inputWithdrawAmount || $[1] !== isMarketLendEnabled || $[2] !== marketParams) {
    t1 = {
      marketParams,
      operation: "withdraw",
      amount: inputWithdrawAmount,
      enabled: isMarketLendEnabled
    };
    $[0] = inputWithdrawAmount;
    $[1] = isMarketLendEnabled;
    $[2] = marketParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    reallocations,
    maxReallocatable,
    amountWithoutReallocation: plannerAmountWithoutReallocation,
    reallocationError,
    isLoading: isReallocationLoading
  } = useMarketReallocations(t1);
  let t2;
  if ($[4] !== positionData) {
    t2 = positionData ? MathLib.zeroFloorSub(positionData.market.totalSupplyAssets, positionData.market.totalBorrowAssets) : undefined;
    $[4] = positionData;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const localLiquidity = t2;
  let t3;
  if ($[6] !== currentSupply || $[7] !== plannerAmountWithoutReallocation) {
    t3 = isDefined(plannerAmountWithoutReallocation) && isDefined(currentSupply) ? MathLib.min(plannerAmountWithoutReallocation, currentSupply) : undefined;
    $[6] = currentSupply;
    $[7] = plannerAmountWithoutReallocation;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const amountWithoutReallocation = t3;
  let t4;
  bb0: {
    if (!isMarketLendEnabled || !positionData) {
      let t5;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = {
          maxWithdrawAssets: undefined,
          isLiquidityCapped: false,
          canFullExitWithExactShares: false
        };
        $[9] = t5;
      } else {
        t5 = $[9];
      }
      t4 = t5;
      break bb0;
    }
    const t5 = (snapshotTimestamp ?? positionData.market.lastUpdate) + ACCRUAL_BUFFER;
    let t6;
    if ($[10] !== maxReallocatable || $[11] !== positionData.market || $[12] !== positionData.supplyAssets || $[13] !== positionData.supplyShares || $[14] !== t5) {
      t6 = computeMaxLendWithdrawAssets({
        supplyAssets: positionData.supplyAssets,
        market: positionData.market,
        supplyShares: positionData.supplyShares,
        accrualTimestamp: t5,
        maxReallocatable
      });
      $[10] = maxReallocatable;
      $[11] = positionData.market;
      $[12] = positionData.supplyAssets;
      $[13] = positionData.supplyShares;
      $[14] = t5;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    t4 = t6;
  }
  const {
    maxWithdrawAssets,
    isLiquidityCapped,
    canFullExitWithExactShares
  } = t4;
  let t5;
  if ($[16] !== maxWithdrawAssets) {
    t5 = {
      maxValue: maxWithdrawAssets,
      onChange: setInputWithdrawAmount
    };
    $[16] = maxWithdrawAssets;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const {
    isMaxActive,
    onMaxClick,
    onManualChange: setInputWithdrawAmountManually
  } = useStickyMaxInput(t5);
  let t6;
  if ($[18] !== inputWithdrawAmount || $[19] !== isMarketLendEnabled || $[20] !== maxWithdrawAssets) {
    t6 = isMarketLendEnabled && isDefined(maxWithdrawAssets) && inputWithdrawAmount > maxWithdrawAssets;
    $[18] = inputWithdrawAmount;
    $[19] = isMarketLendEnabled;
    $[20] = maxWithdrawAssets;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const isWithdrawOverMax = t6;
  const t7 = (reallocations?.length ?? 0) > 0;
  let t8;
  if ($[22] !== canFullExitWithExactShares || $[23] !== isMaxActive || $[24] !== maxWithdrawAssets || $[25] !== t7) {
    t8 = shouldUseExactSharesForMaxLendWithdraw({
      isMaxActive,
      canFullExitWithExactShares,
      maxWithdrawAssets,
      hasReallocations: t7
    });
    $[22] = canFullExitWithExactShares;
    $[23] = isMaxActive;
    $[24] = maxWithdrawAssets;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const shouldUseSupplyShares = t8;
  const [debouncedWithdraw] = useDebounce(inputWithdrawAmount, PREVIEW_DEBOUNCE_MS);
  const isDebouncing = debouncedWithdraw !== inputWithdrawAmount;
  let t9;
  if ($[27] !== debouncedWithdraw || $[28] !== marketParams || $[29] !== reallocations || $[30] !== shouldUseSupplyShares) {
    t9 = {
      marketParams,
      mode: "lendWithdraw",
      collateralAmount: ZERO,
      loanAmount: debouncedWithdraw,
      shouldUseSupplyShares,
      reallocations,
      withRequirements: false
    };
    $[27] = debouncedWithdraw;
    $[28] = marketParams;
    $[29] = reallocations;
    $[30] = shouldUseSupplyShares;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const {
    buildError
  } = useMarketActionBuilder(t9);
  const isEmpty = inputWithdrawAmount === ZERO;
  let t10;
  bb1: {
    if (isEmpty || !isDefined(currentSupply)) {
      t10 = undefined;
      break bb1;
    }
    let t11;
    if ($[32] !== currentSupply || $[33] !== inputWithdrawAmount || $[34] !== shouldUseSupplyShares) {
      t11 = shouldUseSupplyShares ? ZERO : MathLib.zeroFloorSub(currentSupply, inputWithdrawAmount);
      $[32] = currentSupply;
      $[33] = inputWithdrawAmount;
      $[34] = shouldUseSupplyShares;
      $[35] = t11;
    } else {
      t11 = $[35];
    }
    t10 = t11;
  }
  const afterSupply = t10;
  let t11;
  if ($[36] !== buildError || $[37] !== inputWithdrawAmount || $[38] !== isDebouncing || $[39] !== isEmpty || $[40] !== isLiquidityCapped || $[41] !== isWithdrawOverMax || $[42] !== localLiquidity || $[43] !== reallocationError) {
    t11 = getLendWithdrawErrorMessage({
      isWithdrawOverMax,
      isLiquidityCapped,
      reallocationError,
      localLiquidity,
      inputWithdrawAmount,
      isEmpty,
      isDebouncing,
      buildError
    });
    $[36] = buildError;
    $[37] = inputWithdrawAmount;
    $[38] = isDebouncing;
    $[39] = isEmpty;
    $[40] = isLiquidityCapped;
    $[41] = isWithdrawOverMax;
    $[42] = localLiquidity;
    $[43] = reallocationError;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  const buildErrorMessage = t11;
  const canTransferLoanAsset = useAssetCanTransferOnchain(account.address, loanAsset?.address, targetChain.id);
  const isFinalizeDisabled = !isMarketLendEnabled || isEmpty || isPositionLoading || !!positionError || account.isWrongChain || !!buildErrorMessage || isWithdrawOverMax || isReallocationLoading || !canTransferLoanAsset;
  const isButtonLoading = account.isConnected && (isPositionLoading || isReallocationLoading);
  let t12;
  if ($[45] !== onMaxClick) {
    t12 = () => {
      setInputWithdrawAmount(ZERO);
      onMaxClick(false);
    };
    $[45] = onMaxClick;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  const reset = t12;
  let t13;
  if ($[47] !== isLiquidityCapped || $[48] !== reallocationError) {
    t13 = shouldShowLendWithdrawLiquidityWarning({
      isLiquidityCapped,
      reallocationError
    });
    $[47] = isLiquidityCapped;
    $[48] = reallocationError;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] !== afterSupply || $[51] !== amountWithoutReallocation || $[52] !== buildErrorMessage || $[53] !== canTransferLoanAsset || $[54] !== currentSupply || $[55] !== inputWithdrawAmount || $[56] !== isButtonLoading || $[57] !== isDebouncing || $[58] !== isEmpty || $[59] !== isFinalizeDisabled || $[60] !== isMarketLendEnabled || $[61] !== isMaxActive || $[62] !== isPositionLoading || $[63] !== isReallocationLoading || $[64] !== maxWithdrawAssets || $[65] !== onMaxClick || $[66] !== positionData || $[67] !== positionError || $[68] !== reallocations || $[69] !== reset || $[70] !== setInputWithdrawAmountManually || $[71] !== shouldUseSupplyShares || $[72] !== t13) {
    t14 = {
      isMarketLendEnabled,
      inputWithdrawAmount,
      setInputWithdrawAmountManually,
      isEmpty,
      positionData,
      positionError,
      isPositionLoading,
      isReallocationLoading,
      currentSupply,
      afterSupply,
      maxWithdrawAssets,
      isMaxActive,
      onMaxClick,
      shouldUseSupplyShares,
      reallocations,
      amountWithoutReallocation,
      showLiquidityWarning: t13,
      buildErrorMessage,
      canTransferLoanAsset,
      isFinalizeDisabled,
      isButtonLoading,
      isDebouncing,
      reset
    };
    $[50] = afterSupply;
    $[51] = amountWithoutReallocation;
    $[52] = buildErrorMessage;
    $[53] = canTransferLoanAsset;
    $[54] = currentSupply;
    $[55] = inputWithdrawAmount;
    $[56] = isButtonLoading;
    $[57] = isDebouncing;
    $[58] = isEmpty;
    $[59] = isFinalizeDisabled;
    $[60] = isMarketLendEnabled;
    $[61] = isMaxActive;
    $[62] = isPositionLoading;
    $[63] = isReallocationLoading;
    $[64] = maxWithdrawAssets;
    $[65] = onMaxClick;
    $[66] = positionData;
    $[67] = positionError;
    $[68] = reallocations;
    $[69] = reset;
    $[70] = setInputWithdrawAmountManually;
    $[71] = shouldUseSupplyShares;
    $[72] = t13;
    $[73] = t14;
  } else {
    t14 = $[73];
  }
  return t14;
}